import { Link } from "react-router-dom";
import { Card } from "@heroui/react";
import {
  BarChart3,
  ChevronRight,
  Info,
  TrendingDown,
  TrendingUp,
} from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, Tooltip } from "recharts";
import type { MetricItem } from "@/entities/campaign";

export interface CampaignMetricsProps {
  metrics: MetricItem[];
  campaignId?: string;
}

const getMetricColor = (isPositive?: boolean) => {
  if (isPositive === true) return "#10b981"; // emerald-500 (положительная динамика)
  if (isPositive === false) return "#f43f5e"; // rose-500 (отрицательная динамика)
  return "#0ea5e9"; // sky-500 (нейтральная динамика)
};

interface SparklineTooltipProps {
  active?: boolean;
  payload?: any[];
  label?: string;
  formatValue?: (val: number) => string;
  unit?: string;
  color?: string;
}

const SparklineTooltip = ({
  active,
  payload,
  label,
  formatValue,
  unit,
  color,
}: SparklineTooltipProps) => {
  if (!active || !payload || !payload.length) return null;
  const rawVal = payload[0]?.value;
  const itemDate = payload[0]?.payload?.date || label;
  const formatted =
    typeof rawVal === "number"
      ? formatValue
        ? formatValue(rawVal)
        : `${rawVal.toLocaleString("ru-RU")}${unit ? ` ${unit}` : ""}`
      : rawVal;

  return (
    <div className="pointer-events-none rounded-lg border border-neutral-800 bg-neutral-950/95 px-2.5 py-1.5 shadow-2xl backdrop-blur-md text-xs leading-tight select-none z-50">
      {itemDate && (
        <div className="text-[10px] text-neutral-400 font-medium mb-1">
          {itemDate}
        </div>
      )}
      <div className="flex items-center gap-1.5">
        <span
          className="w-2 h-2 rounded-full shrink-0"
          style={{ backgroundColor: color }}
        />
        <span className="font-semibold text-neutral-100 whitespace-nowrap">
          {formatted}
        </span>
      </div>
    </div>
  );
};

const Sparkline = ({
  points,
  dates,
  metricIndex,
  formatValue,
  unit,
  color = "#0ea5e9",
}: {
  points: number[];
  dates?: string[];
  metricIndex: number;
  formatValue?: (val: number) => string;
  unit?: string;
  color?: string;
}) => {
  const chartData = points.map((val, idx) => ({
    date: dates?.[idx] ?? `День ${idx + 1}`,
    val: Number.isFinite(val) ? val : 0,
  }));

  const gradientId = `sparklineGradient-${metricIndex}`;

  return (
    <div className="h-10 w-full min-w-0 relative">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          data={chartData}
          margin={{ top: 4, right: 2, left: 2, bottom: 0 }}
        >
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={color} stopOpacity={0.3} />
              <stop offset="95%" stopColor={color} stopOpacity={0.0} />
            </linearGradient>
          </defs>
          <XAxis dataKey="date" hide />
          <Tooltip
            content={
              <SparklineTooltip
                formatValue={formatValue}
                unit={unit}
                color={color}
              />
            }
            cursor={{
              stroke: "rgba(255, 255, 255, 0.2)",
              strokeWidth: 1,
              strokeDasharray: "2 2",
            }}
            wrapperStyle={{ zIndex: 50, pointerEvents: "none" }}
            isAnimationActive={false}
          />
          <Area
            type="monotone"
            dataKey="val"
            stroke={color}
            strokeWidth={2}
            fill={`url(#${gradientId})`}
            dot={false}
            activeDot={{
              r: 3.5,
              fill: color,
              stroke: "#ffffff",
              strokeWidth: 1.5,
            }}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export const CampaignMetrics = ({
  metrics,
  campaignId,
}: CampaignMetricsProps) => {
  return (
    <div className="flex flex-col gap-1.5">
      {/* 4 квадратные карточки с метриками */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {metrics.map((metric, index) => {
          const isNegativeChange = metric.change?.startsWith("-");
          const chartColor = getMetricColor(metric.isPositive);

          return (
            <Card
              key={index}
              className="p-3.5 flex flex-col justify-between aspect-square select-none relative overflow-visible"
            >
              <div>
                <div className="text-xs text-neutral-400 truncate">
                  {metric.label}
                </div>
                <div className="mt-1 text-2xl font-bold tracking-tight">
                  {metric.value}
                </div>
                {metric.subValue && (
                  <div className="mt-0.5 text-xs text-neutral-400 truncate">
                    {metric.subValue}
                  </div>
                )}
                {metric.change && (
                  <div className="mt-2.5 flex items-center gap-1">
                    <span
                      className={`inline-flex items-center gap-0.5 text-xs font-semibold ${
                        metric.isPositive ? "text-emerald-500" : "text-rose-500"
                      }`}
                    >
                      {isNegativeChange ? (
                        <TrendingDown className="w-3.5 h-3.5 shrink-0" />
                      ) : (
                        <TrendingUp className="w-3.5 h-3.5 shrink-0" />
                      )}
                      {metric.change}
                    </span>
                  </div>
                )}
              </div>

              <div className="mt-auto">
                <Sparkline
                  points={metric.chartPoints}
                  dates={metric.chartDates}
                  metricIndex={index}
                  formatValue={metric.formatValue}
                  unit={metric.unit}
                  color={chartColor}
                />
              </div>
            </Card>
          );
        })}
      </div>

      {/* Карточка-ссылка как пункт меню с минимальными внутренними отступами */}
      <Link
        to={`/campaign/${campaignId || "1"}/statistics`}
        className="block no-underline"
      >
        <Card className="cursor-pointer select-none transition-colors hover:bg-surface-secondary">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <BarChart3 className="w-4 h-4 text-accent" />
              <span className="text-sm font-medium">
                Вся статистика кампании
              </span>
            </div>
            <ChevronRight className="w-4 h-4 text-neutral-400 shrink-0" />
          </div>
        </Card>
      </Link>

      {/* Инфо-текст */}
      <div className="flex items-center gap-1.5 text-xs text-neutral-400 px-1">
        <Info className="w-3.5 h-3.5 shrink-0" />
        <span>Показатели за неделю • Статистика обновляется раз в сутки</span>
      </div>
    </div>
  );
};
